import { useState, useEffect, useCallback, useMemo } from 'react';
import BankSelect from '../common/bankSelect';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Alert, Box, Autocomplete,
} from '@mui/material';
import { quickCreateReferral } from '../../api/referralLinkApi';
import { getAllAgents } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import handleAgentName from '../../utils/userUtils';
import { icInput } from '../../utils/icFormat';

/**
 * Make a referrer without leaving the sale.
 *
 * Three things, because they are the three that matter: who they are, how to reach them,
 * where to pay them. No IC, no home address, no username, no password — a referrer is not
 * being onboarded as staff, and asking for all that mid-sale with a customer waiting is
 * why nobody did it.
 *
 * The login is generated and never shown. Referrers almost never sign in; if one asks, an
 * admin resets it, which already exists.
 * (user 2026-10-19: "full name, phone, bank details to create ... can you autogenerate
 * username and pw? ... cause they usually wontlogin")
 */

const FONT = "'Poppins', sans-serif";

// `submitLabel`: inside a sale the button genuinely creates AND selects the
// referrer into the Referral box, so that is the default. Opened from the
// Referral page there is nothing to select onto, and the page passes its own.
export default function QuickAddReferralDialog({ open, onClose, onCreated, submitLabel = 'Create and select' }) {
    /* Given name and surname separately, not one "full name".
       The convention in this database is FirstName = given name, LastName = surname
       (JAYDEN / NG, CHEE KIAT / WEE). A Malaysian Chinese name spoken in its usual order
       puts the surname first, so splitting "Tan Jin Sheng" on the space lands it
       backwards — and it stays backwards everywhere it is shown, because the name helper
       composes surname + given. Prod already holds a record reversed exactly that way.
       Nothing can tell the two orders apart, so we ask rather than guess.
       (user 2026-10-19: "we usually got first and last name?") */
    const [form, setForm] = useState({
        firstName: '', lastName: '', phone: '', icNumber: '',
        bankName: '', bankAccountNumber: '', bankBeneficiaryName: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    /* WHO THIS REFERRER BELONGS TO.
       A referrer is owned by the sales person who introduced them, and the server
       defaults that to whoever is creating. For an agent adding their own, that is
       right and this picker never appears. For an Accountant or Super Admin it is
       wrong by default: they key referrers in on an AGENT's behalf, and one filed
       under the accountant is invisible to the agent who actually introduced them.
       The server enforces the same rule, so this is a convenience, not the guard.
       (user 2026-10-23: "is accountant able to create referral for agent?") */
    const { user } = useUser();
    const canChooseOwner = useMemo(
        () => (user?.roles || []).some(r => r === 'Super Admin' || r === 'Accountant'),
        [user]);
    const [agents, setAgents] = useState([]);
    const [owner, setOwner] = useState(null);

    const loadAgents = useCallback(async () => {
        try {
            const res = await getAllAgents();
            setAgents(res.data || []);
        } catch (e) {
            // Not fatal: without the list the dialog still creates, it just files the
            // referrer under the person creating them, which is the old behaviour.
            console.error('Failed to load agents for the referrer owner picker', e);
        }
    }, []);

    useEffect(() => {
        if (open && canChooseOwner && agents.length === 0) loadAgents();
    }, [open, canChooseOwner, agents.length, loadAgents]);

    const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

    /* ONLY THE NAME IS REQUIRED. Everything else is taken if offered — the point of this
       dialog is that it does not stop a sale to collect paperwork, and phone, IC and bank
       can all be added later from the user screen. A half-typed phone or IC is still
       rejected by the server, so loose does not mean careless.
       (user 2026-10-20: "name is mandatory only. the rest optional.") */
    const canSave = (form.firstName.trim().length + form.lastName.trim().length) >= 2
        && !saving;

    const reset = () => {
        setForm({
            firstName: '', lastName: '', phone: '', icNumber: '',
            bankName: '', bankAccountNumber: '', bankBeneficiaryName: '',
        });
        setOwner(null);
        setError('');
    };

    const close = () => { reset(); onClose(); };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await quickCreateReferral({
                ...form,
                // Omitted entirely unless someone was picked, so the server keeps its
                // own default rather than being handed a null to interpret.
                ...(owner ? { introducedByUserId: owner.userId } : {}),
            });
            onCreated(res.data);     // the caller selects them on the sale
            reset();
        } catch (err) {
            // The server says what is wrong — most usefully, that this phone already
            // belongs to somebody, which means picking them rather than making a second.
            const d = err?.response?.data;
            setError(d?.message || (typeof d === 'string' && d.trim()) || 'Could not create that referrer.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, pb: 0.5 }}>
                New referrer
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#64748B', mb: 2 }}>
                    They are paid 2% and do not need to sign in. A login is created in case
                    they ever want one.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                {canChooseOwner && (
                    <Autocomplete
                        size="small"
                        options={agents}
                        value={owner}
                        onChange={(_, v) => setOwner(v)}
                        getOptionLabel={(a) => handleAgentName(a.firstName, a.lastName, a.alias) || String(a.userId)}
                        isOptionEqualToValue={(a, b) => a.userId === b.userId}
                        renderInput={(params) => (
                            <TextField {...params} label="Introduced by"
                                helperText={owner
                                    ? 'The referrer will belong to them, and show on their Referral page.'
                                    : 'Leave blank to file this referrer under yourself.'} />
                        )}
                        sx={{ mb: 1.5 }}
                    />
                )}

                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <TextField fullWidth size="small" label="First name" value={form.firstName}
                        onChange={set('firstName')} autoFocus
                        placeholder="e.g. Jin Sheng" helperText="Given name" />
                    <TextField fullWidth size="small" label="Last name" value={form.lastName}
                        onChange={set('lastName')}
                        placeholder="e.g. Tan" helperText="Surname" />
                </Box>
                <Box sx={{ borderTop: '1px solid #E2E8F0', pt: 1.5, mb: 1.5 }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.5px', mb: 0.5 }}>
                        Optional
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', color: '#94A3B8' }}>
                        Needed before they can be paid, but you can add it later.
                    </Typography>
                </Box>

                <TextField fullWidth size="small" label="Phone number" value={form.phone}
                    onChange={set('phone')} sx={{ mb: 1.5 }} inputProps={{ inputMode: 'tel' }}
                    placeholder="e.g. 012-345 6789" />
                <TextField fullWidth size="small" label="IC number" value={form.icNumber}
                    onChange={e => set('icNumber')({ target: { value: icInput(e.target.value) } })} sx={{ mb: 1.5 }}
                    placeholder="e.g. 990101141234" />
                <BankSelect label="Bank" value={form.bankName}
                    onChange={(v) => setForm(f => ({ ...f, bankName: v }))} sx={{ mb: 1.5 }} />
                <TextField fullWidth size="small" label="Account number" value={form.bankAccountNumber}
                    onChange={set('bankAccountNumber')} sx={{ mb: 1.5 }} />
                <TextField fullWidth size="small" label="Account holder name" value={form.bankBeneficiaryName}
                    onChange={set('bankBeneficiaryName')}
                    placeholder="Exactly as it appears on the account" />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={close} sx={{ fontFamily: FONT, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={save} disabled={!canSave}
                    sx={{ fontFamily: FONT, fontWeight: 700, textTransform: 'none' }}>
                    {saving ? 'Creating…' : submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
